import { useState, useEffect } from 'react';
import { History, Clock, User, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';

export default function AuditTrailPanel({ tournamentId }) {
  const [auditData, setAuditData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!tournamentId) return;
    api.get(`/tournaments/${tournamentId}/score-audit`)
      .then(res => setAuditData(res.data))
      .catch(() => setAuditData([]))
      .finally(() => setLoading(false));
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-5 h-5 bg-slate-200 rounded" />
          <div className="h-4 bg-slate-200 rounded w-32" />
        </div>
      </div>
    );
  }

  if (auditData.length === 0) {
    return null; // Don't show if no audit data
  }

  const displayData = expanded ? auditData : auditData.slice(0, 3);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="audit-trail-panel">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-amber-600" />
          <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Score Change History
          </h3>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            {auditData.length} changes
          </span>
        </div>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>

      <div className="divide-y divide-slate-100">
        {displayData.map((entry, i) => (
          <div key={entry.id || i} className="px-6 py-3 hover:bg-slate-50 transition-colors" data-testid={`audit-entry-${i}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{entry.match}</p>
                <div className="flex items-center gap-3 mt-1">
                  {entry.change_type === 'initial_entry' ? (
                    <span className="text-xs text-green-600 font-medium">
                      Score entered: {entry.new_score}
                    </span>
                  ) : (
                    <span className="text-xs text-amber-600 font-medium">
                      {entry.old_score} → {entry.new_score}
                    </span>
                  )}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    entry.change_type === 'initial_entry' 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-amber-50 text-amber-700'
                  }`}>
                    {entry.change_type === 'initial_entry' ? 'New' : 'Edit'}
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <User size={10} />
                  <span>{entry.changed_by}</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
                  <Clock size={9} />
                  <span>{entry.changed_at ? new Date(entry.changed_at).toLocaleString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {auditData.length > 3 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full py-2 text-xs text-green-600 hover:text-green-700 font-medium hover:bg-green-50 transition-colors"
        >
          Show {auditData.length - 3} more changes
        </button>
      )}
    </div>
  );
}
